import { createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';
import { Profile, CommonHash, User } from 'app/types';

// Define a type for the slice state
export interface SessionState {
  profile?: Profile;
  filters: CommonHash;
  user: User | null;
  logged: boolean;
  google_token: string | null;
  loading: boolean;
  checked: boolean;
}

// Define the initial state using that type
const initialState: SessionState = {
  profile: undefined,
  filters: {},
  user: null,
  logged: false,
  google_token: null,
  loading: false,
  checked: false,
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setProfile: (state: SessionState, action: PayloadAction<Profile | undefined>) => {
      state.profile = action.payload;
    },
    setFilters: (state: SessionState, action: PayloadAction<CommonHash>) => {
      state.filters = action.payload;
    },
    loginSuccess: (state: SessionState, action: PayloadAction<{ user: User }>) => {
      state.user = action.payload.user;
      state.profile = action.payload.user.profile;
      state.logged = true;
    },
    logoutUser: (state: SessionState) => {
      state.user = null;
      state.logged = false;
      state.google_token = null;
      state.profile = undefined;
      state.filters = {};
      state.checked = false;
    },
    setGoogleToken: (state: SessionState, action: PayloadAction<{ token: string }>) => {
      state.google_token = action.payload.token;
    },
    startLoading: (state: SessionState) => {
      state.loading = true;
    },
    stopLoading: (state: SessionState) => {
      state.loading = false;
    },
    setUser: (state: SessionState, action: PayloadAction<User | null>) => {
      state.user = action.payload;
    },
    setSessionChecked: (state: SessionState) => {
      state.checked = true;
    },
    deleteAcademicForm: (state: SessionState, action: PayloadAction<number>) => {
      if (!state.user) return;
      state.user.academic_history = state.user.academic_history?.filter(
        (_, index) => index !== action.payload
      );
    },
    deleteEnglishExamFormSession: (state: SessionState, action: PayloadAction<number>) => {
      if (!state.user) return;
      state.user.english_exams = state.user.english_exams?.filter(
        (_, index) => index !== action.payload
      );
    },
    deleteTechnicalExamFormSession: (state: SessionState, action: PayloadAction<number>) => {
      if (!state.user) return;
      state.user.technical_exams = state.user.technical_exams?.filter(
        (_, index) => index !== action.payload
      );
    },
    deleteStoreAttachment: (
      state: SessionState,
      action: PayloadAction<{ fileType: string; index: number | undefined }>
    ) => {
      if (!state.user) return;
      const { fileType, index } = action.payload;

      switch (fileType) {
        case 'passport':
          state.user.passport = '';
          break;
        case 'cv':
          state.user.documents = state.user.documents.filter((document) => document.name !== 'CV');
          break;
        case 'technical_exam':
          if (
            state.user.technical_exams &&
            index !== undefined &&
            Array.isArray(state.user.technical_exams) &&
            typeof state.user.technical_exams[index] === 'object'
          ) {
            state.user.technical_exams[index].technical_exam = '';
          }
          break;
        case 'english_exam':
          if (
            state.user.english_exams &&
            index !== undefined &&
            Array.isArray(state.user.english_exams) &&
            typeof state.user.english_exams[index] === 'object'
          ) {
            state.user.english_exams[index].english_exam = '';
          }
          break;
        case 'transcripts':
          if (
            state.user.academic_history &&
            index !== undefined &&
            Array.isArray(state.user.academic_history) &&
            typeof state.user.academic_history[index] === 'object'
          ) {
            state.user.academic_history[index].transcripts = '';
          }
          break;
        case 'degree_certificate':
          if (
            state.user.academic_history &&
            index !== undefined &&
            Array.isArray(state.user.academic_history) &&
            typeof state.user.academic_history[index] === 'object'
          ) {
            state.user.academic_history[index].degree_certificate = '';
          }
          break;
      }
    },
  },
});

export const {
  setProfile,
  setFilters,
  loginSuccess,
  logoutUser,
  setGoogleToken,
  startLoading,
  stopLoading,
  setUser,
  deleteStoreAttachment,
  setSessionChecked,
  deleteAcademicForm,
  deleteEnglishExamFormSession,
  deleteTechnicalExamFormSession,
} = sessionSlice.actions;

export default sessionSlice.reducer;
